/*
 * © 2025 Fannie Mae. All rights reserved.
 * This software contains confidential information and trade secrets of Fannie Mae.
 * Use, disclosure, or reproduction is prohibited without the prior written consent of Fannie Mae.
 */
import { isInternal, isProjectStandardRole } from "../../../../redux/actions/userAction";
import EditableCellRenderer from "../../../shared/components/AgGrid/EditableCellRenderer";
import RequestDateRenderer from "./RequestDateRenderer";
import EscalatedIndicatorRenderer from "./EscalatedIndicatorRenderer";
import SlaColorFilter from "./SlaColorFilter";
import { SubTypeTooltipRenderer } from "../../../shared/components/AgGrid/SubTypeTooltipRenderer";
import { REQUEST_TYPES } from "../../../shared/constants/RequestTypes";
import { AccessTime } from '@material-ui/icons';
import { dateColumnComparator } from './dateUtils';

const mapSlaColorToHex = (value) => {
  if (!value || typeof value !== 'string') return null;
  const normalized = value.trim().toUpperCase();
  if (normalized === 'RED') return '#dc3545';
  if (normalized === 'YELLOW' || normalized === 'AMBER') return '#ffc107';
  if (normalized === 'GREEN') return '#28a745';
  if (normalized === 'BLUE') return '#007bff';
  if (normalized === 'GREY' || normalized === 'GRAY') return '#bbbbbb';
  return null;
};

export const rowSelection = {
  enableClickSelection: false,
  mode: 'multiRow',
  checkboxes: true,
  selectAll: 'filtered',
  headerCheckbox: false,
};

export const autoSizeStrategy = {
  type: "fitCellContents",
  skipHeaderOnAutoSize: true,
  colIds: ["assignedTo"],
};

export const getColumnDefs = () => ({
  requestType: { 
    headerName: "Req. Type", 
    field: "requestType", 
    headerTooltip: "Request Type",
    filter: 'agSetColumnFilter',
    filterParams: {
      suppressSelectAll: false,
      suppressMiniFilter: false,
      suppressSorting: true,
      textFormatter: (value) => value,
      buttons: ['reset'],
      closeOnApply: true,
      suppressRemoveEntries: true
    },
    suppressMenu: false,
    floatingFilter: false,
    menuTabs: ['filterMenuTab'],
    valueFormatter: (params) => {
      if (!params.value) return '';
      return params.value
        .replace(/\bStatus\b/gi, '')
        .replace(/\bLender\b/gi, '')
        .replace(/\s{2,}/g, ' ')
        .trim();
    },
  },
  subType: { 
    headerName: "Req. Subtype", 
    field: "subType", 
    headerTooltip: "Request Subtype - Hover for details",
    filter: 'agTextColumnFilter',
    filterParams: {
      buttons: ['reset']
    },
    suppressMenu: false,
    floatingFilter: false,
    menuTabs: ['filterMenuTab'],
    minWidth: 280,
    cellRenderer: SubTypeTooltipRenderer,
    valueGetter: (params) => {
      const reasonCodes = params.data?.reasonCodes;
      if (reasonCodes && reasonCodes.length > 0) {
        return reasonCodes.map(rc => rc.reasonCode).filter(Boolean).join(', ');
      }
      const docs = params.data?.requestFeedDocumentDTOList;
      if (docs && docs.length > 0) {
        const uniqueSubTypes = [...new Set(docs.map(d => d.requestSubTypeName).filter(Boolean))];
        if (uniqueSubTypes.length > 0) return uniqueSubTypes.join(', ');
      }

      const requestTypeName = params.data?.requestType;
      if (requestTypeName === REQUEST_TYPES.REMOVE_DELIVERY_RESTRICTION) {
        const removeDrDetails = params.data?.removeDrDetails;
        return removeDrDetails?.map(dr => dr.restrcnShortName).join(', ') || '';
      }

      return params.data?.requestSubTypeName || '';
    }
  },
  projectId: { headerName: "Proj. ID", field: "projectId", headerTooltip: "Project ID", maxWidth: 120, },
  requestId: { headerName: "Req. ID", field: "requestId", headerTooltip: "Request ID", maxWidth: 120, },
  projectName: { headerName: "Proj. Name", field: "projectName", headerTooltip: "Project Name", },
  escalatedIndicator: {
    headerName: "",
    field: "requestStatus",
    headerTooltip: "Escalation Indicator",
    maxWidth: 40,
    cellRenderer: EscalatedIndicatorRenderer,
    sortable: false,
    filter: false,
    resizable: false,
    suppressMenu: true,
    floatingFilter: false,
  },
  requestStatus: { 
    headerName: "Req. Status", 
    field: "requestStatus", 
    headerTooltip: "Request Status",
    filter: 'agSetColumnFilter',
    filterParams: {
      suppressSelectAll: false,
      suppressMiniFilter: false,
      suppressSorting: true,
      textFormatter: (value) => value,
      buttons: ['reset'],
      closeOnApply: true,
      suppressRemoveEntries: true
    },
    suppressMenu: false,
    floatingFilter: false,
    menuTabs: ['filterMenuTab'],
  },
  assignedTo: { 
    headerName: "Assigned To", 
    field: "assignedTo", 
    headerTooltip: "Assigned To", 
    minWidth: 180,
    filter: 'agSetColumnFilter',
    filterParams: {
      suppressSelectAll: false,
      suppressMiniFilter: false,
      suppressSorting: true,
      textFormatter: (value) => value || 'Unassigned',
      buttons: ['reset'],
      closeOnApply: true,
      suppressRemoveEntries: true
    },
    suppressMenu: false,
    floatingFilter: false,
    menuTabs: ['filterMenuTab'],
    cellRenderer: EditableCellRenderer,
  },
  previouslyWorkedBy: {
    headerName: "Prev. Worked By",
    field: "previouslyWorkedBy",
    headerTooltip: "Previously Worked By",
    minWidth: 160,
    valueFormatter: (params) => {
      const prevWorkedBy = params.value;
      const assignedTo = params.data?.assignedTo;
      if (!prevWorkedBy || prevWorkedBy === assignedTo) return '\u2014';
      return prevWorkedBy;
    },
  },
  requestStatusDate: {
    headerName: "Status Date",
    field: "requestStatusDate",
    headerTooltip: "Request Status Date",
    minWidth: 150,
    cellDataType: "date",
    filter: "agDateColumnFilter",
    filterParams: { comparator: dateColumnComparator },
    valueFormatter: (params) => {
      if (!params.value) return '\u2014';
      const date = new Date(params.value);
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      const yyyy = date.getFullYear();
      const hh = String(date.getHours()).padStart(2, '0');
      const min = String(date.getMinutes()).padStart(2, '0');
      return `${mm}/${dd}/${yyyy} ${hh}:${min}`;
    },
  },
  dateCreated: {
    headerName: "Req. Date",
    headerTooltip: "Requested Date",
    field: "dateCreated",
    cellDataType: "date",
    filter: "agDateColumnFilter",
    filterParams: { comparator: dateColumnComparator },
    sort: "desc",
    minWidth: 160,
    cellRenderer: RequestDateRenderer,
  },
  sla: {
    headerName: "SLA",
    field: "slaColor",
    headerTooltip: "SLA Status",
    minWidth: 70,
    maxWidth: 90,
    // eslint-disable-next-line react/display-name
    cellRenderer: (params) => {
      const hex = mapSlaColorToHex(params.value);
      return <AccessTime style={{ color: hex || '#bbbbbb', fontSize: '1.1rem' }} />;
    },
    filter: SlaColorFilter,
    filterParams: {},
    suppressMenu: false,
    floatingFilter: false,
    menuTabs: ['filterMenuTab'],
  },
  lender: { headerName: "Lender", field: "lender", headerTooltip: "Lender", },
  createdBy: { headerName: "Req. By", field: "createdBy", headerTooltip: "Requested By", },
})

export const columnOrder = [
  "requestType",
  "subType",
  "escalatedIndicator",
  "requestStatus",
  "projectId",
  "projectName",
  "assignedTo",
  "previouslyWorkedBy",
  "dateCreated",
  "sla",
  "requestStatusDate",
  "lender",
  "createdBy"
];
